<template>
    <transition :name="mode === 'drawer' ? 'modal-drawer' : 'modal-fade'">
        <div v-if="show"
             v-modal-shell-portal="mode === 'drawer'"
             ref="dialog"
             class="fixed inset-0 flex outline-none"
             :class="[
                 mode === 'drawer'
                     ? 'z-[9999] items-stretch justify-end p-0'
                     : 'z-[10000] items-center justify-center p-4',
                 overlayClass,
             ]"
             role="dialog"
             aria-modal="true"
             :aria-labelledby="titleId"
             :aria-describedby="descriptionId"
             tabindex="-1"
             @click.self="handleBackdrop"
             @keydown.tab="handleTab"
             @keydown.esc.prevent.stop="handleEscape">
            <div class="foxy-modal-focus-scope transform transition-all"
                 :class="[mode === 'drawer' ? 'm-0' : 'mx-4', surfaceClass, modalClass]">
                <slot></slot>
            </div>
        </div>
    </transition>
</template>

<script>
const activeModalShells = [];

export default {
    name: 'ModalShell',
    directives: {
        modalShellPortal: {
            inserted(element, binding) {
                if (binding.value && element.parentNode !== document.body) {
                    document.body.appendChild(element);
                }
            },
        },
    },
    props: {
        show: { type: Boolean, default: false },
        titleId: { type: String, required: true },
        descriptionId: { type: String, default: null },
        modalClass: { type: [String, Array, Object], default: 'max-w-md' },
        surfaceClass: { type: [String, Array, Object], default: 'foxy-modal' },
        overlayClass: { type: [String, Array, Object], default: 'bg-black/50 backdrop-blur-sm' },
        mode: {
            type: String,
            default: 'dialog',
            validator: (value) => ['dialog', 'drawer'].includes(value),
        },
        initialFocusSelector: { type: String, default: '[data-modal-cancel], [data-modal-primary]' },
        focusDialogOnOpen: { type: Boolean, default: false },
        backdropDismissible: { type: Boolean, default: false },
        escapeDismissible: { type: Boolean, default: true },
    },
    data() {
        return {
            previouslyFocusedElement: null,
            backgroundInertState: [],
            modalRootInertState: null,
            lifecycleGeneration: 0,
        };
    },
    watch: {
        show(isOpen) {
            if (isOpen) {
                this.activate();
            } else {
                this.deactivate();
            }
        },
    },
    beforeDestroy() {
        this.lifecycleGeneration += 1;
        this.releaseEnvironment();
    },
    methods: {
        setReturnFocusTarget(element) {
            if (element?.isConnected && typeof element.focus === 'function') {
                this.previouslyFocusedElement = element;
            }
        },
        captureReturnFocus() {
            if (this.previouslyFocusedElement) return;
            const active = document.activeElement;
            if (!this.$refs.dialog?.contains(active) && active instanceof HTMLElement) {
                this.previouslyFocusedElement = active;
            }
        },
        activate() {
            const generation = ++this.lifecycleGeneration;
            this.registerActiveShell();
            this.captureReturnFocus();
            this.$nextTick(() => {
                if (!this.show || generation !== this.lifecycleGeneration) return;
                this.applyBackgroundInertness();
                document.addEventListener('focusin', this.keepFocusInside, true);
                this.focusInitialControl();
            });
        },
        deactivate() {
            const generation = ++this.lifecycleGeneration;
            this.$nextTick(() => {
                Promise.resolve().then(() => {
                    if (this.show || generation !== this.lifecycleGeneration) return;
                    this.releaseEnvironment();
                    this.restoreReturnFocus();
                });
            });
        },
        handleTab(event) {
            if (!this.isTopmostShell()) return;
            event.preventDefault();
            const dialog = this.$refs.dialog;
            const controls = this.focusableControls();
            if (!dialog || !controls.length) {
                dialog?.focus();
                return;
            }

            const activeIndex = controls.indexOf(document.activeElement);
            if (activeIndex === -1) {
                (event.shiftKey ? controls[controls.length - 1] : controls[0]).focus();
                return;
            }

            const direction = event.shiftKey ? -1 : 1;
            const nextIndex = (activeIndex + direction + controls.length) % controls.length;
            controls[nextIndex].focus();
        },
        handleBackdrop() {
            if (!this.isTopmostShell()) return;
            if (this.backdropDismissible) this.$emit('backdrop');
        },
        handleEscape() {
            if (!this.isTopmostShell()) return;
            if (this.escapeDismissible) this.$emit('escape');
        },
        focusInitialControl() {
            const dialog = this.$refs.dialog;
            if (!dialog) return;
            if (this.focusDialogOnOpen) {
                dialog.focus();
                return;
            }
            const controls = this.focusableControls();
            const preferredCandidate = dialog.querySelector(this.initialFocusSelector);
            const preferred = controls.includes(preferredCandidate) ? preferredCandidate : null;
            (preferred || controls[0] || dialog).focus();
        },
        focusableControls() {
            const dialog = this.$refs.dialog;
            if (!dialog) return [];
            const selector = [
                'a[href]',
                'button',
                'input:not([type="hidden"])',
                'select',
                'textarea',
                'summary',
                '[contenteditable="true"]',
                '[tabindex]',
            ].join(',');

            return [...dialog.querySelectorAll(selector)].filter((element) => {
                if (element === dialog
                    || element.disabled
                    || element.hidden
                    || element.getAttribute('tabindex') === '-1'
                    || element.getAttribute('aria-hidden') === 'true'
                    || element.getAttribute('aria-disabled') === 'true'
                    || element.closest('[hidden], [inert], [aria-hidden="true"]')) {
                    return false;
                }
                const style = window.getComputedStyle(element);
                return style.display !== 'none'
                    && style.visibility !== 'hidden'
                    && (element.offsetParent !== null || element.getClientRects().length > 0);
            });
        },
        modalBodyRoot() {
            let node = this.$refs.dialog;
            while (node?.parentNode && node.parentNode !== document.body) {
                node = node.parentNode;
            }
            return node?.parentNode === document.body ? node : null;
        },
        applyBackgroundInertness() {
            if (this.backgroundInertState.length) return;
            const modalRoot = this.modalBodyRoot();
            if (modalRoot) {
                this.modalRootInertState = {
                    element: modalRoot,
                    hadInert: modalRoot.hasAttribute('inert'),
                    inertValue: modalRoot.getAttribute('inert'),
                };
                modalRoot.removeAttribute('inert');
            }
            this.backgroundInertState = [...document.body.children]
                .filter((element) => element !== modalRoot)
                .map((element) => ({
                    element,
                    hadInert: element.hasAttribute('inert'),
                    inertValue: element.getAttribute('inert'),
                }));
            this.backgroundInertState.forEach(({ element }) => element.setAttribute('inert', ''));
        },
        releaseEnvironment() {
            document.removeEventListener('focusin', this.keepFocusInside, true);
            this.backgroundInertState.forEach(({ element, hadInert, inertValue }) => {
                if (hadInert) {
                    element.setAttribute('inert', inertValue ?? '');
                } else {
                    element.removeAttribute('inert');
                }
            });
            this.backgroundInertState = [];
            if (this.modalRootInertState) {
                const { element, hadInert, inertValue } = this.modalRootInertState;
                if (hadInert) {
                    element.setAttribute('inert', inertValue ?? '');
                } else {
                    element.removeAttribute('inert');
                }
                this.modalRootInertState = null;
            }
            this.unregisterActiveShell();
        },
        restoreReturnFocus(attempt = 0) {
            const target = this.previouslyFocusedElement;
            if (!target) return;
            if (!target.isConnected) {
                this.previouslyFocusedElement = null;
                return;
            }
            if (!target.disabled) {
                target.focus({ preventScroll: true });
                this.previouslyFocusedElement = null;
                return;
            }
            if (attempt < 2 && typeof window.requestAnimationFrame === 'function') {
                window.requestAnimationFrame(() => this.restoreReturnFocus(attempt + 1));
            } else {
                this.previouslyFocusedElement = null;
            }
        },
        keepFocusInside(event) {
            const dialog = this.$refs.dialog;
            if (!this.show || !this.isTopmostShell() || !dialog || dialog.contains(event.target)) return;
            this.focusInitialControl();
        },
        registerActiveShell() {
            this.unregisterActiveShell();
            activeModalShells.push(this);
        },
        unregisterActiveShell() {
            const index = activeModalShells.lastIndexOf(this);
            if (index !== -1) activeModalShells.splice(index, 1);
        },
        isTopmostShell() {
            return activeModalShells[activeModalShells.length - 1] === this;
        },
    },
};
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.3s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
    opacity: 0;
}

.modal-drawer-enter-active,
.modal-drawer-leave-active {
    transition: opacity 0.22s ease;
}

.modal-drawer-enter-active .foxy-modal-focus-scope,
.modal-drawer-leave-active .foxy-modal-focus-scope {
    transition: transform 0.22s ease;
}

.modal-drawer-enter,
.modal-drawer-leave-to {
    opacity: 0;
}

.modal-drawer-enter .foxy-modal-focus-scope,
.modal-drawer-leave-to .foxy-modal-focus-scope {
    transform: translateX(100%);
}

@media (prefers-reduced-motion: reduce) {
    .modal-fade-enter-active,
    .modal-fade-leave-active,
    .modal-drawer-enter-active,
    .modal-drawer-leave-active,
    .modal-drawer-enter-active .foxy-modal-focus-scope,
    .modal-drawer-leave-active .foxy-modal-focus-scope {
        transition-duration: 0.01ms;
    }
}
</style>
